import { useState } from "react";
import { MousePointerClick } from "lucide-react";
import { cn } from "@/lib/utils";


export interface RevealCardProps {
  image?: { src: string; alt: string };
  fallback?: React.ReactNode;
  label: React.ReactNode;
  overlay: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export function RevealCard({ image, fallback, label, overlay, action, className }: RevealCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <article
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border bg-card aspect-[3/4] transition-all duration-300 cursor-pointer",
        open
          ? "-translate-y-2 shadow-lift border-ember/40"
          : "hover:-translate-y-2 hover:shadow-lift hover:border-ember/40",
        className
      )}
    >
      {image ? (
        <img
          src={image.src}
          alt={image.alt}
          loading="lazy"
          decoding="async"
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-transform duration-500",
            open ? "scale-105" : "group-hover:scale-105"
          )}
        />
      ) : (
        fallback
      )}

      {/* Bouncing "clickable" indicator */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-ink shadow-lg backdrop-blur-sm transition-opacity duration-300",
          open ? "opacity-0" : "opacity-100 md:animate-gallery-bounce"
        )}
      >
        <MousePointerClick className="h-4 w-4" strokeWidth={2.5} />
      </span>


      {/* Bottom label */}
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/85 via-black/55 to-transparent transition-opacity duration-300",
          open ? "opacity-0" : "opacity-100 group-hover:opacity-0"
        )}
      >
        {label}
      </div>

      {/* Hover / tap overlay */}
      <div
        className={cn(
          "absolute inset-0 p-6 bg-ink/95 text-bone flex flex-col overflow-y-auto transition-all duration-300",
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 pointer-events-none"
        )}
      >
        {overlay}
        {action && (
          <div onClick={(e) => e.stopPropagation()} className="mt-auto pt-5">
            {action}
          </div>
        )}
      </div>
    </article>
  );
}
